import React, { useEffect, useMemo, useState } from 'react'
import { BotIcon, CheckIcon, ClipboardIcon, ExternalLinkIcon } from './icons'
import { getBuildWithAiStyles, type BuildWithAITheme } from './styles'
import { useCopyToClipboard } from './useCopyToClipboard'

export interface BuildWithAIAgentConfig {
  studioName: string
  githubUrl: string
  description?: string
}

export type BuildWithAIAssistant = 'Claude' | 'Codex'
export type AssistantTab = 'claude' | 'codex'

export interface BuildWithAICoreProps {
  agent: BuildWithAIAgentConfig
  theme?: BuildWithAITheme
  /** Prompt the "Copy" button copies and the "Prompt to paste into the extension" card displays. */
  promptText: string
  /**
   * Extra "How it works" steps for one assistant tab, rendered after the shared "paste the
   * prompt" step and before the "reads agent details from GitHub" step -- e.g. App Studio's MCP
   * consent-gate step. Return null/undefined for no extra steps on that tab.
   */
  renderExtraOverviewSteps?: (assistant: BuildWithAIAssistant) => React.ReactNode
  /**
   * Extra card(s) for one assistant tab, rendered after the "Agent repository" card and before
   * "Prompt to paste into the extension" -- e.g. App Studio's MCP connection card. Return
   * null/undefined for no extra card on that tab. Use `getBuildWithAiStyles(theme)` so extra
   * cards stay visually consistent with the ones rendered here.
   */
  renderExtraCards?: (assistant: BuildWithAIAssistant) => React.ReactNode
  /**
   * Overrides the last "How it works" step (default: "{assistant} follows the execution steps
   * defined in the agent to build the workflow for you."). App Studio uses this to call out that
   * building happens only through the MCP tools -- a studio-specific qualifier the shared default
   * can't know about.
   */
  renderFinalStep?: (assistant: BuildWithAIAssistant) => React.ReactNode
  /**
   * Reports the active tab on mount and on every change. The MCP bridge's `window.bizfirstAgent`
   * must only be registered while the Claude tab is showing (that's the only tab whose
   * instructions mention it) -- `BuildWithAICore` owns `activeTab` internally, so a caller that
   * needs to gate a side effect on it (App Studio's MCP registration effect) needs it reported
   * back rather than re-deriving it.
   */
  onTabChange?: (tab: AssistantTab) => void
}

const TABS: { id: AssistantTab; label: BuildWithAIAssistant }[] = [
  { id: 'claude', label: 'Claude' },
  { id: 'codex', label: 'Codex' },
]

/**
 * Shared "Build with AI" page shell: tab bar, "How it works" overview, agent-repository card,
 * prompt-to-copy card, and the "Codex coming soon" placeholder. Per-studio differences (the
 * prompt text, and any extra cards/steps such as App Studio's MCP bridge) are supplied by the
 * caller via props rather than edited in here, so this file stays studio-agnostic.
 */
export const BuildWithAICore: React.FC<BuildWithAICoreProps> = ({
  agent,
  theme = 'dark',
  promptText,
  renderExtraOverviewSteps,
  renderExtraCards,
  renderFinalStep,
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState<AssistantTab>('claude')
  // Fires on mount too (not just on change) so a caller's gated effect (e.g. App Studio's MCP
  // registration) sees the correct initial tab without assuming it starts at 'claude'.
  useEffect(() => { onTabChange?.(activeTab) }, [activeTab, onTabChange])
  const styles = useMemo(() => getBuildWithAiStyles(theme), [theme])
  const { card, cardTitle, bodyText, accent, isDark } = styles
  const { copied, copyError, copy } = useCopyToClipboard(promptText)

  const isPlaceholderUrl = !agent.githubUrl || agent.githubUrl.includes('REPLACE_ME')

  const renderOverview = (assistant: BuildWithAIAssistant) => (
    <div style={card}>
      <h2 style={cardTitle}>How it works</h2>
      <ol style={{ ...bodyText, paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <li>Open the {assistant} in Chrome browser extension on this page.</li>
        <li>Paste the prompt below into {assistant}.</li>
        {renderExtraOverviewSteps?.(assistant)}
        <li>{assistant} reads the {agent.studioName} agent's details and skills from the GitHub path below — fully, including its design-pattern and style/theme docs, not just the entry points.</li>
        {renderFinalStep?.(assistant) ?? (
          <li>{assistant} follows the execution steps defined in the agent to build the workflow for you.</li>
        )}
      </ol>
    </div>
  )

  const renderGithubCard = () => (
    <div style={card}>
      <h2 style={cardTitle}>Agent repository</h2>
      <p style={{ ...bodyText, marginBottom: '12px' }}>
        {agent.description ??
          `Agent details and skills used to build ${agent.studioName} workflows.`}
      </p>
      {isPlaceholderUrl ? (
        <div
          style={{
            fontSize: '13px',
            fontFamily: 'monospace',
            color: isDark ? '#fbbf24' : '#b45309',
            background: 'rgba(245, 158, 11, 0.08)',
            border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(245, 158, 11, 0.4)',
            borderRadius: '8px',
            padding: '10px 14px',
          }}
        >
          GitHub path not configured yet — placeholder: {agent.githubUrl || 'TODO_GITHUB_PATH'}
        </div>
      ) : (
        <a
          href={agent.githubUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            fontFamily: 'monospace',
            color: accent,
            textDecoration: 'none',
            wordBreak: 'break-all',
          }}
        >
          <ExternalLinkIcon size={14} />
          {agent.githubUrl}
        </a>
      )}
    </div>
  )

  const renderPromptCard = (enabled: boolean) => (
    <div style={card}>
      <h2 style={cardTitle}>Prompt to paste into the extension</h2>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
          background: isDark ? 'rgba(2, 6, 23, 0.5)' : 'rgba(0, 0, 0, 0.03)',
          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '14px 16px',
        }}
      >
        <p style={{ ...bodyText, color: isDark ? '#e2e8f0' : '#1f2937', fontFamily: 'monospace', fontSize: '13px' }}>
          "{promptText}"
        </p>
        <button
          onClick={copy}
          disabled={!enabled}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flexShrink: 0,
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 500,
            border: `1px solid ${isDark ? 'rgba(58,140,69,0.4)' : 'rgba(58,140,69,0.5)'}`,
            background: isDark ? 'rgba(58,140,69,0.12)' : 'rgba(58,140,69,0.08)',
            color: isDark ? '#6ee7b7' : '#16a34a',
            cursor: enabled ? 'pointer' : 'not-allowed',
            opacity: enabled ? 1 : 0.5,
            whiteSpace: 'nowrap',
          }}
        >
          {copied ? <CheckIcon size={13} /> : <ClipboardIcon size={13} />}
          {copyError ? 'Copy failed — select the text manually' : copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  )

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* ── Tab bar ── */}
      <div
        role="tablist"
        aria-label="Assistant"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          padding: '0 32px 0 28px',
          borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
          background: isDark ? 'rgba(2,6,23,0.4)' : 'rgba(255,255,255,0.6)',
          flexShrink: 0,
        }}
      >
        {TABS.map(({ id, label }) => {
          const isActive = activeTab === id
          return (
            <button
              key={id}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(id)}
              style={{
                padding: '14px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive ? `2px solid ${accent}` : '2px solid transparent',
                color: isActive ? accent : isDark ? '#64748b' : '#94a3b8',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s ease',
                marginBottom: '-1px',
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* ── Tab content ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 32px 28px 28px' }}>
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <BotIcon size={20} color={accent} />
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: isDark ? '#f8fafc' : '#111827', margin: 0 }}>
              Build {agent.studioName} workflows using {activeTab === 'claude' ? 'Claude' : 'Codex'}
            </h1>
          </div>
          <p style={{ fontSize: '14px', color: isDark ? '#64748b' : '#94a3b8', margin: 0 }}>
            Let an AI coding assistant build this workflow for you, driven from your browser.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '760px' }}>
          {activeTab === 'claude' ? (
            <>
              {renderOverview('Claude')}
              {renderGithubCard()}
              {renderExtraCards?.('Claude')}
              {renderPromptCard(true)}
            </>
          ) : (
            <>
              <div
                style={{
                  ...card,
                  background: 'rgba(245, 158, 11, 0.06)',
                  border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(245, 158, 11, 0.4)',
                }}
              >
                <h2 style={{ ...cardTitle, color: isDark ? '#fbbf24' : '#d97706' }}>Coming soon</h2>
                <p style={bodyText}>
                  Codex support for {agent.studioName} is not wired up yet. It will follow the same
                  pattern as Claude — open the Codex extension or CLI, point it at the agent
                  repository below, and paste the execution prompt.
                </p>
              </div>
              {renderOverview('Codex')}
              {renderGithubCard()}
              {renderExtraCards?.('Codex')}
              {renderPromptCard(false)}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default BuildWithAICore
